import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ClipboardList, Play, Lock, KeyRound, CalendarDays, Video, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { CertificatePreview } from "../components/CertificatePreview";
import useSeo from "../hooks/useSeo";

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("el-GR", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtDateTime = (value) =>
  value ? new Date(value).toLocaleString("el-GR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";

export default function CourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [code, setCode] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  const load = () => {
    api
      .get(`/courses/${courseId}`)
      .then((r) => {
        setData(r.data);
        if (r.data.enrolled) {
          api.get(`/courses/${courseId}/sessions`).then((s) => setSessions(s.data)).catch(() => setSessions([]));
        }
      })
      .catch(() => setData(false));
  };

  useEffect(load, [courseId]);

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }
    setEnrolling(true);
    try {
      await api.post(`/courses/${courseId}/enroll`, { access_code: code });
      toast.success(t("course.enrolled"));
      setCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEnrolling(false);
    }
  };

  useSeo({
    lang,
    title: data?.course?.title || t("common.loading"),
    description:
      data?.course?.description?.replace(/\s+/g, " ").slice(0, 160) ||
      (lang === "en"
        ? "Sports science course with structured lessons, quizzes and a verifiable certificate."
        : "Μάθημα αθλητικής επιστήμης με δομημένα μαθήματα, quiz και πιστοποιητικό."),
    image: data?.course?.thumbnail,
  });

  if (data === null) return <p className="max-w-7xl mx-auto px-6 py-16 text-[#64748B]" data-testid="course-loading">{t("common.loading")}</p>;
  if (data === false) return <p className="max-w-7xl mx-auto px-6 py-16 text-[#DC2626]" data-testid="course-error">{t("common.error")}</p>;

  const { course, lessons, enrolled, completed_lessons, progress } = data;
  const upcoming = sessions.filter((s) => new Date(s.starts_at) >= new Date());
  const nextSession = upcoming[0];

  return (
    <div data-testid="course-detail-page">
      {/* Hero panel */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-6">
        <div className="relative rounded-[28px] md:rounded-[40px] overflow-hidden bg-white border border-[#E2E8F0]">
          {course.thumbnail && <img src={course.thumbnail} alt={course.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3A] via-[#0B1B3A]/80 to-[#0B1B3A]/40" />
          <div className="relative p-7 sm:p-12 pt-16 sm:pt-24">
            <Link data-testid="course-back-link" to="/courses" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/80 hover:text-white transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" /> {t("course.back")}
            </Link>
            <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl text-white mt-5 max-w-3xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]" data-testid="course-title">
              {course.title}
            </h1>
            <p className="text-base sm:text-lg text-white/90 mt-5 max-w-3xl leading-relaxed" data-testid="course-hero-description">{course.description}</p>
            <div className="flex flex-wrap items-center gap-2 mt-6" data-testid="course-hero-badges">
              <span className="px-3 py-1.5 rounded-full bg-white text-[#1D4ED8] text-[11px] font-bold uppercase tracking-[0.14em]">{course.category}</span>
              <span className="px-3 py-1.5 rounded-full border border-white/50 text-white text-[11px] font-bold uppercase tracking-[0.14em]">{t(`level.${course.level}`)}</span>
              {course.duration_months ? (
                <span className="px-3 py-1.5 rounded-full border border-white/50 text-white text-[11px] font-bold uppercase tracking-[0.14em]">{course.duration_months} {t("program.months")}</span>
              ) : null}
              <span className="px-3 py-1.5 rounded-full border border-white/50 text-white text-[11px] font-bold uppercase tracking-[0.14em]">{lessons.length} {t("courses.lessons")}</span>
              {course.price ? (
                <span className="px-3 py-1.5 rounded-full bg-[#1D4ED8] text-white text-[11px] font-bold uppercase tracking-[0.14em]">{course.price}€</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("course.curriculum")}</h2>

          {lessons.length === 0 ? (
            <div className="card-dark p-10 mt-8 text-[#64748B]" data-testid="course-no-lessons">{t("program.noLessons")}</div>
          ) : (
            <div className="mt-8 space-y-3">
              {lessons.map((l, i) => {
                const mod = /^Module (\d+)\.(\d+) · (.+?) — (.+)$/.exec(l.title);
                const prevMod = i > 0 ? /^Module (\d+)\./.exec(lessons[i - 1].title)?.[1] : null;
                const header = mod && mod[1] !== prevMod ? (
                  <div key={`m-${mod[1]}`} className={`flex items-center gap-3 ${i > 0 ? "mt-10" : ""}`} data-testid={`module-header-${mod[1]}`}>
                    <span className="px-3 py-1 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold uppercase tracking-[0.18em]">Module {mod[1]}</span>
                    <h3 className="font-heading font-black text-lg text-[#0B1B3A]">{mod[3]}</h3>
                  </div>
                ) : null;
                const displayTitle = mod ? `${mod[1]}.${mod[2]} ${mod[4]}` : l.title;
                const completed = completed_lessons.includes(l.id);
                const accessible = enrolled && l.unlocked;
                const row = (
                  <div
                    className={`card-dark flex items-center gap-5 p-5 ${accessible ? "hover:border-[#1D4ED8] hover:translate-x-1 cursor-pointer" : "opacity-70"} transition-all duration-200`}
                    data-testid={`lesson-row-${l.id}`}
                  >
                    <span className="font-heading font-black text-2xl text-[#94A3B8] w-10">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#0B1B3A]">{displayTitle}</p>
                      <p className="text-xs text-[#64748B] mt-1 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <ClipboardList className="w-3 h-3" /> {l.quiz_count} {t("course.quizCount")}
                        </span>
                        {!l.unlocked && (
                          <span className="inline-flex items-center gap-1 text-[#B45309]" data-testid={`lesson-locked-${l.id}`}>
                            <Lock className="w-3 h-3" /> {t("program.unlocksOn")} {fmtDate(l.unlock_date)}
                          </span>
                        )}
                      </p>
                    </div>
                    {completed ? (
                      <CheckCircle2 className="w-6 h-6 text-[#1D4ED8]" data-testid={`lesson-completed-${l.id}`} />
                    ) : !l.unlocked ? (
                      <Lock className="w-5 h-5 text-[#94A3B8]" />
                    ) : accessible ? (
                      <span className="w-9 h-9 rounded-full bg-[#EEF4FF] flex items-center justify-center">
                        <Play className="w-3.5 h-3.5 text-[#1D4ED8] fill-current" />
                      </span>
                    ) : null}
                  </div>
                );
                return accessible ? (
                  <div key={l.id}>{header}<Link to={`/lessons/${l.id}`} className="block">{row}</Link></div>
                ) : (
                  <div key={l.id}>{header}{row}</div>
                );
              })}
            </div>
          )}

          <CertificatePreview title={course.title} hours={course.duration_months ? course.duration_months * 16 : 0} />

          {/* Live sessions */}
          {enrolled && (
            <div className="mt-14" data-testid="course-sessions-section">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-6 h-6 text-[#1D4ED8]" />
                <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("sessions.title")}</h2>
              </div>
              {sessions.length === 0 ? (
                <p className="text-[#64748B] mt-4" data-testid="course-sessions-empty">{t("sessions.empty")}</p>
              ) : (
                <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]">
                  {sessions.map((s) => (
                    <div key={s.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`session-row-${s.id}`}>
                      <div className="flex-1 min-w-[200px]">
                        <p className="font-bold text-[#0B1B3A]">{s.title}</p>
                        <p className="text-xs text-[#64748B] mt-1 flex flex-wrap items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" /> {fmtDateTime(s.starts_at)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {s.duration_min} {t("sessions.min")}
                          </span>
                        </p>
                        {s.description && <p className="text-sm text-[#475569] mt-2">{s.description}</p>}
                      </div>
                      <div className="flex gap-2">
                        {s.meeting_url && (
                          <a data-testid={`session-join-${s.id}`} href={s.meeting_url} target="_blank" rel="noreferrer" className="btn-lime !px-5 !py-2 text-xs">
                            <Video className="w-3.5 h-3.5" /> {t("sessions.join")}
                          </a>
                        )}
                        {s.recording_url && (
                          <a data-testid={`session-recording-${s.id}`} href={s.recording_url} target="_blank" rel="noreferrer" className="btn-outline-light !px-5 !py-2 text-xs">
                            {t("sessions.recording")}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>

        <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <div className="card-cream p-8 sticky top-24">
            <p className="text-[#334155] leading-relaxed" data-testid="course-description">{course.description}</p>

            <div className="grid grid-cols-2 gap-5 mt-8 pt-6 border-t border-[#E2E8F0]">
              {course.price ? (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("program.price")}</p>
                  <p className="font-heading font-black text-3xl mt-1" data-testid="course-price">{course.price}€</p>
                </div>
              ) : null}
              {course.duration_months ? (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("program.duration")}</p>
                  <p className="font-heading font-black text-3xl mt-1">{course.duration_months} <span className="text-base">{t("program.months")}</span></p>
                </div>
              ) : null}
              {course.start_date && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("program.start")}</p>
                  <p className="font-bold mt-1" data-testid="course-start-date">{fmtDate(course.start_date)}</p>
                </div>
              )}
              {course.schedule && (
                <div className="col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("program.schedule")}</p>
                  <p className="text-sm font-semibold mt-1" data-testid="course-schedule">{course.schedule}</p>
                </div>
              )}
            </div>

            {enrolled ? (
              <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#475569]">{t("course.progress")}</span>
                  <span className="font-heading font-black text-3xl text-[#0B1B3A]" data-testid="course-progress-value">{progress}%</span>
                </div>
                <div className="h-3 bg-[#F8FAFC] rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-[#1D4ED8] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="course-progress-bar" />
                </div>
                <p className="text-sm font-bold text-[#0B1B3A] mt-4 inline-flex items-center gap-2" data-testid="course-enrolled-label">
                  <CheckCircle2 className="w-4 h-4" /> {t("course.enrolled")}
                </p>

                {nextSession && (
                  <div className="mt-5 rounded-[18px] bg-[#EEF4FF] p-5" data-testid="course-next-session">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{t("sessions.next")}</p>
                    <p className="font-bold text-white mt-2">{nextSession.title}</p>
                    <p className="text-xs text-white/70 mt-1">{fmtDateTime(nextSession.starts_at)}</p>
                    {nextSession.meeting_url && (
                      <a href={nextSession.meeting_url} target="_blank" rel="noreferrer" className="btn-lime mt-4 !px-5 !py-2 text-xs">
                        <Video className="w-3.5 h-3.5" /> {t("sessions.join")}
                      </a>
                    )}
                  </div>
                )}

                {lessons.some((l) => l.unlocked) && (
                  <Link data-testid="course-start-button" to={`/lessons/${lessons.find((l) => l.unlocked).id}`} className="btn-blue w-full mt-5">
                    {t("course.start")} <Play className="w-4 h-4" />
                  </Link>
                )}
                {progress === 100 && (
                  <Link data-testid="course-certificate-button" to={`/certificate/${course.id}`} className="btn-dark w-full mt-3">
                    {t("cert.view")}
                  </Link>
                )}
              </div>
            ) : (
              <form onSubmit={handleEnroll} className="mt-8 pt-6 border-t border-[#E2E8F0]" data-testid="course-access-form">
                <p className="font-heading font-black text-lg flex items-center gap-2">
                  <KeyRound className="w-5 h-5" /> {t("program.accessTitle")}
                </p>
                <input
                  data-testid="course-access-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t("program.accessPlaceholder")}
                  className="input-cream mt-4 uppercase tracking-widest font-bold"
                />
                <button data-testid="enroll-button" type="submit" disabled={enrolling} className="btn-blue w-full mt-4 disabled:opacity-50">
                  {enrolling ? t("common.loading") : t("program.accessCta")}
                </button>
                <p className="text-xs text-[#475569] mt-3">{t("program.accessHint")}</p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
